"use client";

import { AnimatedFeaturesRow } from "@/components/global-ui/animated-feature-row";
import { useMediaQuery } from "usehooks-ts";
interface FeatureConfig {
  nameIndex: number;
  descriptionIndex: number;
  videoIndex?: number;
  bannerImageIndex?: number;
}
interface Stage2FeaturesProps {
  titleIndex?: number;
  descriptionIndex?: number;
  subtitleIndex?: number;
  features?: FeatureConfig[];
  buttonText?: string;
  buttonLink?: string;
}
export function Stage2Features({
  titleIndex = 3,
  descriptionIndex = 1,
  subtitleIndex = 1,
  features = [{
    nameIndex: 1,
    descriptionIndex: 5,
    videoIndex: 1,
    bannerImageIndex: 4
  },
  // Brand Voice Engine with brand voice demo
  {
    nameIndex: 2,
    descriptionIndex: 8,
    videoIndex: 2,
    bannerImageIndex: 5
  },
  // Content Hub with content hub demo
  {
    nameIndex: 3,
    descriptionIndex: 13,
    videoIndex: 3,
    bannerImageIndex: 8
  } // Smart Editor with smart edit demo
  ],
  buttonText = "Create Content",
  buttonLink = "/content"
}: Stage2FeaturesProps) {
  // Check if device is large (lg breakpoint = 1024px and above)
  const isLargeDevice = useMediaQuery("(min-width: 1024px)");

  // Available titles
  const titles: {
    [key: number]: string;
  } = {
    1: "Stage 2: Content (Create Messages That Spread)",
    2: "The Content Stage",
    3: "Stage 2: Launch Copy That Converts",
    4: "Phase 2: Content That Clicks",
    5: "Phase 2: Content That Converts"
  };

  // Available descriptions
  const descriptions: {
    [key: number]: string;
  } = {
    1: "Import your own style or write like the people you admire. Use LaunchAI, trained on the top-performing posts from each platform, to create content that sparks clicks, likes, and shares.",
    2: "Import your own style or write like the people you admire. Use our template library to create platform-specific content that generates clicks, likes and shares. ",
    3: "Create platform-specific posts that get real attention — not just likes.",
    4: "Create scroll-stopping content for X, PH, Reddit, LinkedIn, and more. Built for virality and traction."
  };

  // Available subtitles/CTAs
  const subtitles: {
    [key: number]: string;
  } = {
    1: "Launch like the top 1% of founders. One place to write content for every platform that matters.",
    2: "Cut through the noise. Launch with words that win attention and drive action.",
    3: "Out-write 99% of other launches. Create content that gets attention and drives action."
  };

  // Available feature names
  const featureNames: {
    [key: number]: string;
  } = {
    1: "Brand Voice",
    2: "Content Hub",
    3: "Smart Edit",
    4: "AI Editor",
    5: "Launch like the top 1% of founders",
    6: "Cut through the noise",
    7: "Out-write 99% of other launches"
  };

  // Available feature descriptions
  const featureDescriptions: {
    [key: number]: string;
  } = {
    // Brand Voice Engine descriptions (1-5)
    1: "Analyze successful accounts or define your own voice guidelines. Every piece of content sounds authentically you.",
    2: "Steal the voice of your favorite X account — or define your own.",
    3: "Instantly mirror your writing style or swipe one from someone you admire.",
    4: "Plug in a Twitter handle. Steal the tone. Generate content in that voice, instantly.",
    5: "Plug in a Twitter handle and import your writing style, or write like the people you admire. Stay on brand, every time.",
    // Content Hub descriptions (6-10)
    6: "Launch-ready templates for Twitter, Product Hunt, Reddit, and 20+ platforms. Just click generate and get content that converts.",
    7: "Pre-built templates for every launch platform. Twitter threads, Product Hunt posts, LinkedIn announcements, Show HN posts, and 20+ more.",
    8: "Ready-to-use templates for Product Hunt, X, LinkedIn, Show HN, and 15+ other platforms. Launch week content created in 30 minutes.",
    9: "Twitter, PH, Reddit, LinkedIn — launch content for every platform in minutes.",
    10: "Post on X, Product Hunt, Reddit, and LinkedIn — with launch-ready content.",
    // AI Editor descriptions (11-14)
    11: "Highlight any text and command: 'make this punchier,' 'add emotion,' or 'make this viral.' Accept or reject suggestions with one click.",
    12: 'Highlight any text and give commands like "make this more emotional," "shorten by 50%," or "make this punchier."',
    13: 'Edit like you think — "make this punchier," "make it emotional" "add more energy". Accept or reject suggestions with one click.',
    14: 'Tell it what you want — "more energy," "add social proof," "shorten this."',
    // CTA section (15-17)
    15: "Launch like the top 1% of founders. One place to write content for every platform that matters.",
    16: "Cut through the noise. Launch with words that win attention and drive action.",
    17: "Out-write 99% of other launches. Create content that gets attention and drives action."
  };

  // Available video URLs
  const videoUrls: {
    [key: number]: string;
  } = {
    1: isLargeDevice ? "https://viral.devassets.app/hls_brandvoice/desktop.m3u8" : "https://viral.devassets.app/hls_brandvoice/master.m3u8",
    2: isLargeDevice ? "https://viral.devassets.app/hls_content_generator/desktop.m3u8" : "https://viral.devassets.app/hls_content_generator/master.m3u8",
    3: isLargeDevice ? "https://viral.devassets.app/hls_smart_edit/desktop.m3u8" : "https://viral.devassets.app/hls_smart_edit/master.m3u8",
    4: isLargeDevice ? "https://viral.devassets.app/hls_content_generator/desktop.m3u8" : "https://viral.devassets.app/hls_content_generator/master.m3u8"
  };

  // Available still image URLs for video placeholders
  const stillImageUrls: {
    [key: number]: string;
  } = {
    1: "https://viral.devassets.app/stills/viral_brandvoice_still.png",
    2: "https://viral.devassets.app/stills/viral_content_generator_still.png",
    3: "https://viral.devassets.app/stills/viral_smart_edit_still.png",
    4: "https://viral.devassets.app/stills/viral_content_generator_still.png" // Reusing content generator still for 4th feature
  };

  // Available banner image URLs from demos folder
  const bannerImageUrls: {
    [key: number]: string;
  } = {
    1: "/images/demos/viral-name-demo.png",
    2: "/images/demos/logo-generator-demo.png",
    3: "/images/demos/persona-generator-demo.png",
    4: "/images/demos/brand-voice-extractor-demo.png",
    5: "/images/demos/content-hub-demo.png",
    6: "/images/demos/launch-pad-demo.png",
    7: "/images/demos/promo-video-demo.png",
    8: "/images/demos/smart-edit-demo.png",
    9: "/images/demos/viral-giveaway-demo.png",
    10: "/images/demos/section-1.png",
    11: "/images/demos/section-2.png",
    12: "/images/demos/section-3.png"
  };

  // Build features array based on configuration
  const configuredFeatures = features.map((featureConfig, index) => ({
    id: index + 1,
    name: featureNames[featureConfig.nameIndex] || `Feature ${featureConfig.nameIndex}`,
    description: featureDescriptions[featureConfig.descriptionIndex] || `Description ${featureConfig.descriptionIndex}`,
    video: videoUrls[featureConfig.videoIndex || 1],
    stillImage: stillImageUrls[featureConfig.videoIndex || 1],
    bannerImage: bannerImageUrls[featureConfig.bannerImageIndex || 1]
    // Add button to last feature -- Disabled for Feature-Row
    // ...(index === features.length - 1 && {
    //   buttonText,
    //   buttonLink,
    // }),
  }));
  return <AnimatedFeaturesRow title={titles[titleIndex] || titles[1]} description={descriptions[descriptionIndex] || descriptions[1]} subtitle={subtitles[subtitleIndex] || subtitles[1]} features={configuredFeatures} autoRotateInterval={12000} // Increased from 6000 to reduce memory pressure
  data-sentry-element="AnimatedFeaturesRow" data-sentry-component="Stage2Features" data-sentry-source-file="stage-2-features.tsx" />;
}